import React, { useEffect, useState } from "react";
import { FaEdit, FaTimes, FaPlay } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getVideosAPI, addVideoAPI, updateVideoAPI, deleteVideoAPI } from '../../utils/api';
import { BASE_URL } from '../../utils/configConstants';
import Sidebar from "../Sidebar/Sidebar";
import AddVideoModal from "./components/AddVideoModal";
import "./Video.css";

const Video = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedVideoThumbnail, setSelectedVideoThumbnail] = useState(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  const [isNewThumbnail, setIsNewThumbnail] = useState(false);
  const [isNewVideo, setIsNewVideo] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [videoPreviewModal, setVideoPreviewModal] = useState(false);
  const [selectedVideoForPreview, setSelectedVideoForPreview] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const handleVideoClick = (video) => {
    setSelectedVideoForPreview(video);
    setVideoPreviewModal(true);
  };

  const handleCloseVideoPreview = () => {
    setVideoPreviewModal(false);
    setSelectedVideoForPreview(null);
  };

  // Convert image to base64
  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // Fetch videos from API
  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getVideosAPI();

      if (response?.status === 200) {
        setVideos(response?.data || []);
      } else if (response?.message) {
        setError(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error');
        setVideos([]);
      } else {
        setError('Failed due to internal error');
        setVideos([]);
      }
    } catch (err) {
      setError('Failed due to internal error');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes?.includes(file?.type)) {
        showToast("Please select only JPG, PNG, JPEG, or WebP files for thumbnail", "error");
        return;
      }

      // Validate file size (max 40MB)
      const maxSize = 40 * 1024 * 1024; // 40MB in bytes
      if (file.size > maxSize) {
        showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
        e.target.value = '';
        return;
      }

      if (file?.type?.startsWith("image/")) {
        setSelectedVideoThumbnail(URL?.createObjectURL(file));
        setIsNewThumbnail(true);
      }
    }
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type - only MP4, WebM, and MOV
      const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
      if (!allowedTypes?.includes(file?.type)) {
        showToast("Please select only MP4, WebM, or MOV files for video", "error");
        e.target.value = '';
        return;
      }

      // Validate file size (max 40MB)
      const maxSize = 40 * 1024 * 1024; // 40MB in bytes
      if (file.size > maxSize) {
        showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
        e.target.value = '';
        return;
      }

      setSelectedVideoFile(file);
      setIsNewVideo(true);
    }
  };

  const handleEdit = (video) => {
    setSelectedVideo(video);
    setSelectedVideoThumbnail(video?.image ? (BASE_URL + video.image) : null);
    setSelectedVideoFile(null);
    setExistingVideoUrl(video?.video ? (video.video.startsWith('http') ? video.video : BASE_URL + video.video) : null);
    setVideoUrl(video?.url || '');
    setIsNewThumbnail(false);
    setIsNewVideo(false);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setSelectedVideo(null);
    setSelectedVideoThumbnail(null);
    setSelectedVideoFile(null);
    setExistingVideoUrl(null);
    setVideoUrl('');
    setIsNewThumbnail(false);
    setIsNewVideo(false);
    setIsEditMode(false);
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedVideoThumbnail(null);
    setSelectedVideoFile(null);
    setExistingVideoUrl(null);
    setVideoUrl('');
    setIsNewThumbnail(false);
    setIsNewVideo(false);
    setIsEditMode(false);
    setSelectedVideo(null);
  };

  const handleDeleteClick = (video) => {
    setVideoToDelete(video);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setVideoToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!videoToDelete?._id) {
        showToast("Invalid video data", "error");
        return;
      }

      const reqData = {
        video_id: videoToDelete._id
      };

      const response = await deleteVideoAPI(reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setVideoToDelete(null);
        showToast(`Video deleted successfully!`, "success");
        await fetchVideos(); // Refresh the list
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    }
  };

  const handleSave = async () => {

    if (!isEditMode) {
      // For add mode, both thumbnail and video are required
      if (!selectedVideoThumbnail) {
        showToast("Please upload a video thumbnail", "error");
        return;
      }
      if (!selectedVideoFile) {
        showToast("Please upload a video", "error");
        return;
      }
    }

    setSaveLoading(true);

    try {
      console.log(videoUrl);
      const formData = new FormData();
      formData.append('url', videoUrl.trim());

      if (isEditMode) {
        // Update existing video
        formData.append('video_id', selectedVideo?._id);

        // Only include thumbnail if a new thumbnail is selected
        if (isNewThumbnail) {
          const thumbnailInput = document.getElementById('edit-video-thumbnail-input');
          const thumbnailFile = thumbnailInput?.files[0];
          if (thumbnailFile) {
            const base64Thumbnail = await convertImageToBase64(thumbnailFile);
            formData.append('video_thumbnail', base64Thumbnail);
          }
        }

        // Only include video if a new video is selected
        if (isNewVideo && selectedVideoFile) {
          formData.append('video', selectedVideoFile);
        }

        const response = await updateVideoAPI(formData);

        if (response?.status === 200) {
          await fetchVideos();
          handleCloseModal();
          showToast("Video updated successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      } else {
        // Add new video
        const thumbnailInput = document.getElementById('video-thumbnail-input');
        const thumbnailFile = thumbnailInput?.files[0];
        if (!thumbnailFile) {
          showToast("Please select a thumbnail file", "error");
          setSaveLoading(false);
          return;
        }

        const base64Thumbnail = await convertImageToBase64(thumbnailFile);
        formData.append('video_thumbnail', base64Thumbnail);
        formData.append('video', selectedVideoFile);

        const response = await addVideoAPI(formData);

        if (response?.status === 200) {
          await fetchVideos();
          handleCloseModal();
          showToast("Video added successfully!", "success");
        } else if (response?.message) {
          showToast(typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error', "error");
        } else {
          showToast('Failed due to internal error', "error");
        }
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="Video-layout">
        <Sidebar />
        <div className="Video-main-content">
          <div className="Video-header-row">
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Video"
            >
              <span>Add Video</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading videos...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎥</div>
              <h3>No Videos Found</h3>
              <p>Click "Add Video" to create the first one.</p>
            </div>
          ) : (
            <div className="video-grid">
              {videos.map((video, idx) => (
                <div key={video?._id || `video-${idx}`} className="video-card">
                  <div className="video-image-container">
                    {video?.image ? (
                      <>
                        <img
                          src={BASE_URL + video?.image}
                          alt={`Video ${video?._id}`}
                          className="video-img"
                          onClick={() => handleVideoClick(video)}
                        />
                        <div className="video-play-overlay" onClick={() => handleVideoClick(video)}>
                          <div className="play-icon-container">
                            <FaPlay className="play-icon" />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="video-placeholder" onClick={() => handleVideoClick(video)}>
                        <span>🎥</span>
                        <p>No Thumbnail</p>
                      </div>
                    )}
                    <div className="video-actions-overlay">
                      <button
                        type="button"
                        className="edit-btn"
                        title="Edit Video"
                        onClick={() => handleEdit(video)}
                        aria-label="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="delete-btn"
                        title="Delete Video"
                        onClick={() => handleDeleteClick(video)}
                        aria-label="Delete"
                      >
                        <MdDelete />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add/Edit Modal */}
          <AddVideoModal
            showModal={showModal}
            isEditMode={isEditMode}
            selectedVideoThumbnail={selectedVideoThumbnail}
            setSelectedVideoThumbnail={setSelectedVideoThumbnail}
            selectedVideoFile={selectedVideoFile}
            setSelectedVideoFile={setSelectedVideoFile}
            existingVideoUrl={existingVideoUrl}
            isNewThumbnail={isNewThumbnail}
            setIsNewThumbnail={setIsNewThumbnail}
            isNewVideo={isNewVideo}
            setIsNewVideo={setIsNewVideo}
            handleThumbnailFileChange={handleThumbnailFileChange}
            handleVideoFileChange={handleVideoFileChange}
            saveLoading={saveLoading}
            onSave={handleSave}
            onClose={handleCloseModal}
            videoUrl={videoUrl}
            setVideoUrl={setVideoUrl}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete this video?`}
            confirmText="Delete"
          />

          {videoPreviewModal && selectedVideoForPreview && (
            <div className="modal-overlay" onClick={handleCloseVideoPreview}>
              <div className="video-preview-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Video Preview</h3>
                  <button className="modal-close" onClick={handleCloseVideoPreview}>
                    <FaTimes />
                  </button>
                </div>
                <div className="modal-body">
                  {selectedVideoForPreview.video ? (
                    <video
                      src={selectedVideoForPreview.video.startsWith('http')
                        ? selectedVideoForPreview.video
                        : BASE_URL + selectedVideoForPreview.video}
                      controls
                      style={{
                        width: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain'
                      }}
                    />
                  ) : selectedVideoForPreview.image ? (
                    <img
                      src={BASE_URL + selectedVideoForPreview.image}
                      alt="Video Thumbnail"
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '70vh',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <div style={{ padding: '2rem', textAlign: 'center' }}>
                      <p>No video or thumbnail available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Video;

